import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  getAllUsers,
  getCompaniesForMapping,
  getMappedCompanies,
  addUserCompanyMapping,
  deleteUserCompanyMapping
} from './services/masterDataService';
import { useCurrentDomain } from "./context/DomainContext";

const UserAccountMapping = () => {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [mappedCompanies, setMappedCompanies] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [mappedLoading, setMappedLoading] = useState(false);
  const { domain } = useCurrentDomain();

  const BRAND_GREEN = '#0aa375';

  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: '30px',
      fontSize: '12px',
      borderColor: '#dee2e6',
      boxShadow: 'none',
      '&:hover': { borderColor: BRAND_GREEN },
    }),
    indicatorsContainer: (base) => ({ ...base, padding: '0 2px' }),
    dropdownIndicator: (base) => ({ ...base, padding: '0 4px' }),
    clearIndicator: (base) => ({ ...base, padding: '0 4px' }),
    option: (base, state) => ({
      ...base,
      fontSize: '12px',
      backgroundColor: state.isSelected ? BRAND_GREEN : state.isFocused ? '#e6f7f2' : 'white',
      color: state.isSelected ? 'white' : '#333',
    }),
    singleValue: (base) => ({ ...base, fontSize: '12px' }),
    placeholder: (base) => ({ ...base, fontSize: '12px', color: '#adb5bd' }),
  };

  const Field = ({ label, required }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <span style={{ fontSize: '10px', fontWeight: 600, color: 'black', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
        {label}
        {required && <span style={{ color: '#dc3545', marginLeft: '3px', fontSize: '14px', lineHeight: '1' }}>*</span>}
      </span>
    </div>
  );

  const Spinner = ({ size = 'sm', color = BRAND_GREEN }) => (
    <div className="text-center py-4">
      <div className={`spinner-border spinner-border-${size}`} style={{ color }} role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );

  useEffect(() => {
      if (domain) {
        fetchUsers(domain);
      }
    }, [domain]);
  useEffect(() => {
    if (!selectedUser) {
      setCompanies([]);
      setMappedCompanies([]);
      setSelectedCompanies([]);
      setSelectedRows([]);
      setSelectAll(false);
      setCompanySearch('');
      return;
    }
    fetchCompanies(selectedUser.value);
    fetchMappedCompanies(selectedUser.value);
  }, [selectedUser, domain]);

  const fetchUsers = async (domain) => {
    setUsersLoading(true);
    try {
      const data = await getAllUsers(domain);
      setUsers(data.map(u => ({ value: u.code, label: u.description })));
    } catch (err) {
      toast.error('Failed to load users.');
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchCompanies = async (userId) => {
    setCompaniesLoading(true);
    try {
      setCompanies(await getCompaniesForMapping(userId, domain));
      setSelectedCompanies([]);
      setCompanySearch('');
    } catch (err) {
      toast.error('Failed to load companies.');
    } finally {
      setCompaniesLoading(false);
    }
  };

  const fetchMappedCompanies = async (userId) => {
    setMappedLoading(true);
    try {
      setMappedCompanies(await getMappedCompanies(userId, domain));
      setSelectedRows([]);
      setSelectAll(false);
    } catch (err) {
      toast.error('Failed to load mapped companies.');
    } finally {
      setMappedLoading(false);
    }
  };

  const handleUserChange = (selected) => {
    setSelectedUser(selected);
    setSelectedCompanies([]);
    setSelectedRows([]);
    setSelectAll(false);
    setCompanySearch('');
  };

  const handleAddMapping = async () => {
    if (!selectedUser || selectedCompanies.length === 0) return;
    setAddLoading(true);
    const loadingToast = toast.loading('Adding mapping...');
    try {
      await addUserCompanyMapping(domain, selectedUser.value, selectedCompanies);
      toast.dismiss(loadingToast);
      toast.success(`${selectedCompanies.length} company mapping(s) added successfully.`);
      await fetchCompanies(selectedUser.value);
      await fetchMappedCompanies(selectedUser.value);
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err?.response?.data?.message || 'Failed to add mapping.');
    } finally {
      setAddLoading(false);
    }
  };

  const handleDeleteMapping = () => {
    if (!selectedUser || selectedRows.length === 0) return;
    const toastId = toast(
      <div style={{ textAlign: 'center', minWidth: '250px' }}>
        <div style={{ fontWeight: '600', marginBottom: '10px' }}>
          Delete {selectedRows.length} mapping(s)?
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '10px' }}>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => toast.dismiss(toastId)}>
            Cancel
          </button>
          <button className="btn btn-sm btn-danger" onClick={() => { toast.dismiss(toastId); performDelete(); }}>
            Yes, Delete
          </button>
        </div>
      </div>,
      { autoClose: false, closeButton: false, closeOnClick: false, draggable: false, className: 'bg-white shadow' }
    );
  };

  const performDelete = async () => {
    setDeleteLoading(true);
    const loadingToast = toast.loading('Deleting mappings...');
    try {
      await deleteUserCompanyMapping(domain, selectedUser.value, selectedRows);
      toast.dismiss(loadingToast);
      toast.success(`${selectedRows.length} mapping(s) deleted successfully.`);
      await fetchCompanies(selectedUser.value);
      await fetchMappedCompanies(selectedUser.value);
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err?.response?.data?.message || 'Failed to delete mapping.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const filteredCompanies = companies.filter(c =>
    c.code !== selectedUser?.value)
    .filter(c =>
    (c.description || '').toLowerCase().includes(companySearch.toLowerCase())
  );

  const allMappedSelected =
    mappedCompanies.length > 0 &&
    mappedCompanies.every(m => selectedRows.includes(m.mappedUserId));

  const handleSelectAll = () => {
    if (allMappedSelected) {
      setSelectedRows([]);
      setSelectAll(false);
    } else {
      setSelectedRows(mappedCompanies.map(m => m.mappedUserId));
      setSelectAll(true);
    }
  };

  return (
    <div className="container-fluid p-0 px-1">
      <div className="card-body">
        <div className="row g-2">
          <div className="col-12 col-lg-6">
            <div className="card border h-100" style={{ borderRadius: '6px' }}>
              <div className="card-header" style={{ color: '#fff', fontWeight: 600,fontSize: 14, padding: '8px', backgroundColor: BRAND_GREEN }}>
                Add Mapping
              </div>
              <div className="card-body p-2">

                <div className="mb-2">
                  <Field label="User" required />
                  <Select
                    options={users}
                    value={selectedUser}
                    onChange={handleUserChange}
                    isLoading={usersLoading}
                    placeholder="Select user..."
                    isSearchable
                    isClearable
                    styles={selectStyles}
                  />
                </div>

                <div className="mb-2">
                  <Field label="Search Companies" />
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Filter company list..."
                    value={companySearch}
                    onChange={e => setCompanySearch(e.target.value)}
                    disabled={!selectedUser}
                    style={{ fontSize: '12px' }}
                  />
                </div>

                <div style={{ height: '280px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', background: '#fafafa' }}>
                  {companiesLoading ? (
                    <Spinner />
                  ) : filteredCompanies.length === 0 ? (
                    <div className="text-center py-4" style={{ color: '#adb5bd', fontSize: '12px' }}>
                      {selectedUser ? 'No companies available.' : 'Select a user first.'}
                    </div>
                  ) : (
                    filteredCompanies.map(c => (
                      <label
                        key={c.code}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', fontSize: '12px' }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedCompanies.includes(c.code)}
                          onChange={() =>
                            setSelectedCompanies(prev =>
                              prev.includes(c.code)
                                ? prev.filter(id => id !== c.code)
                                : [...prev, c.code]
                            )
                          }
                          style={{ accentColor: BRAND_GREEN }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {c.description}
                          </div>
                        </div>
                      </label>
                    ))
                  )}
                </div>

                <div className="d-flex justify-content-center gap-2 mt-2">
                  <button
                    onClick={() => { setSelectedCompanies([]); setCompanySearch(''); }}
                    className="btn btn-outline-secondary btn-sm px-3"
                    style={{ fontSize: '12px' }}
                  >
                    Reset
                  </button>
                  <button
                    onClick={handleAddMapping}
                    disabled={addLoading || !selectedUser || selectedCompanies.length === 0}
                    className="btn btn-sm text-white px-3 fw-semibold"
                    style={{ backgroundColor: BRAND_GREEN, border: 'none', fontSize: '12px', opacity: (addLoading || !selectedUser || selectedCompanies.length === 0) ? 0.65 : 1 }}
                  >
                    {addLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                        Saving...
                      </>
                    ) : (
                      `Add Mapping (${selectedCompanies.length})`
                    )}
                  </button>
                </div>

              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="card border h-100" style={{ borderRadius: '6px' }}>
              <div className="card-header" style={{ color: '#fff', fontWeight: 600,fontSize: 14, padding: '8px', backgroundColor: BRAND_GREEN }}>
                Mapped Companies
              </div>
              <div className="card-body p-2">

                {mappedCompanies.length > 0 && (
                  <div className="d-flex align-items-center justify-content-between mb-1 px-1 py-1"
                    style={{ borderBottom: '1px solid #e9ecef', minHeight: '28px' }}>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="checkbox"
                        id="selectAllMapped"
                        checked={allMappedSelected}
                        onChange={handleSelectAll}
                        disabled={mappedCompanies.filter(m => !m.isDefault).length === 0}
                        style={{ accentColor: BRAND_GREEN, width: '14px', height: '14px', cursor: 'pointer' }}
                      />
                      <label className="form-check-label mb-0" style={{ fontSize: '11px', fontWeight: 600, color: '#495057', cursor: 'pointer', userSelect: 'none' }} htmlFor="selectAllMapped">
                        Select All
                      </label>
                      {mappedCompanies.length > 0 && (
                        <span style={{ fontSize: '10px', color: '#adb5bd' }}>
                          ({mappedCompanies.length} total)
                        </span>
                      )}
                    </div>
                    {selectedRows.length > 0 && (
                      <span style={{
                        fontSize: '10px', fontWeight: 600,
                        background: '#e6f7f2', color: BRAND_GREEN,
                        padding: '2px 8px', borderRadius: '20px',
                        border: `1px solid ${BRAND_GREEN}22`
                      }}>
                        ✓ {selectedRows.length} selected
                      </span>
                    )}
                  </div>
                )}

                <div style={{ height: '300px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                  {mappedLoading ? (
                    <Spinner />
                  ) : mappedCompanies.length === 0 ? (
                    <div className="alert alert-success small py-1 mb-0 m-2">
                      {selectedUser ? 'No active mappings.' : 'Select a user to view mappings.'}
                    </div>
                  ) : (
                    <table className="table table-sm table-hover mb-0" style={{ fontSize: '12px' }}>
                      <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>
                        <tr>
                          <th style={{ width: '36px', padding: '6px 10px' }}></th>
                          <th style={{ padding: '6px 10px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.3px', color: '#6c757d' }}>User Name</th>
                          <th style={{ padding: '6px 10px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.3px', color: '#6c757d' }}>Company Name</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mappedCompanies.map(item => (
                          <tr key={item.mappedUserId} style={{ background: selectedRows.includes(item.mappedUserId) ? '#e6f7f2' : 'white' }}>
                            <td style={{ padding: '6px 10px' }}>
                              <input
                                type="checkbox"
                                checked={selectedRows.includes(item.mappedUserId)}
                                onChange={() =>
                                  setSelectedRows(prev =>
                                    prev.includes(item.mappedUserId)
                                      ? prev.filter(id => id !== item.mappedUserId)
                                      : [...prev, item.mappedUserId]
                                  )
                                }
                                disabled={false}
                                style={{ accentColor: BRAND_GREEN }}
                              />
                            </td>
                            <td style={{ padding: '6px 10px', fontWeight: 500 }}>
                              {item.sZ_USER_NAME || item.sz_USER_NAME || item.SZ_USER_NAME}
                              {item.isDefault && (
                                <span style={{ marginLeft: '8px', fontSize: '10px', background: '#dcfce7', color: BRAND_GREEN, padding: '2px 8px', borderRadius: '12px', fontWeight: 700 }}>
                                  Default
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '6px 10px', color: '#6c757d', maxWidth: '180px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {item.sZ_COMPANY_NAME || item.sz_COMPANY_NAME || item.SZ_COMPANY_NAME || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="d-flex justify-content-center mt-2">
                  <button
                    onClick={handleDeleteMapping}
                    disabled={deleteLoading || selectedRows.length === 0}
                    className="btn btn-sm btn-danger px-3"
                    style={{ fontSize: '12px' }}
                  >
                    {deleteLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                        Deleting...
                      </>
                    ) : (
                      `Delete Selected (${selectedRows.length})`
                    )}
                  </button>
                </div>

              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default UserAccountMapping;